import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { generateDeviceId } from '@renderer/utils/deviceUtils'
import { PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS } from '@renderer/utils/printResultMinDisplay'

// Define ResizeQuality type
type ResizeQuality = 'pixelated' | 'low' | 'medium' | 'high'

// Define PreviewQuality interface
interface PreviewQuality {
  maxWidth: number
  maxHeight: number
  quality: number
  resizeQuality: ResizeQuality
  /** Cap live preview frame rate when not driving a recording canvas. Undefined = uncapped. */
  maxFps?: number
}

interface SettingsState {
  password?: string
  selectedPrinter?: string
  downloadLocal: boolean
  hasSeenSkipPaymentGuide: boolean
  autoStart: boolean
  autoLaunch: boolean
  defaultProjectId?: string
  activationCode: string
  deviceId: string
  /** When true, Preview overlays stats.js FPS (use with Performance panel on target hardware). */
  debugFps: boolean
  useWebcam: boolean
  setPassword: (password?: string) => void
  setSelectedPrinter: (printer?: string) => void
  selectedCamera?: string
  setSelectedCamera: (camera?: string) => void
  selectedSecondaryCamera?: string
  setSelectedSecondaryCamera: (camera?: string) => void
  cameraConnectionKey: number // Increments on each camera connection to force Preview remount
  incrementCameraConnectionKey: () => void
  setDownloadLocal: (download: boolean) => void
  setHasSeenSkipPaymentGuide: (seen: boolean) => void
  setAutoStart: (enabled: boolean) => void
  setAutoLaunch: (enabled: boolean) => void
  setDefaultProjectId: (projectId?: string) => void
  setActivationCode: (code?: string) => void
  setDebugFps: (enabled: boolean) => void
  setUseWebcam: (enabled: boolean) => void
  ejectCameraWhenIdle: boolean
  setEjectCameraWhenIdle: (enabled: boolean) => void
  showCameraQualityWarnings: boolean
  setShowCameraQualityWarnings: (enabled: boolean) => void
  cameraSettings: {
    captureRatio: number | 'auto' | undefined
    mirror: boolean
    useShutterCapture: boolean
    applyFilterOnPreview: boolean
    previewQuality?: PreviewQuality
    captureQuality?: PreviewQuality
    /** Rotate the live preview display (CSS). Does not affect the saved image. */
    previewRotation: number
    /** Rotate the saved/captured image pixels. Independent from previewRotation. */
    captureRotation: number
    autoBrightenLiveViewForFlash: boolean
    liveViewShutterSpeedOverride?: number
    postShutterLiveviewDelayMs?: number
  }
  setCameraSettings: (settings: Partial<SettingsState['cameraSettings']>) => void
  secondaryCameraSettings: {
    captureRatio: number | 'auto' | undefined
    mirror: boolean
    useShutterCapture: boolean
    applyFilterOnPreview: boolean
    previewQuality?: PreviewQuality
    captureQuality?: PreviewQuality
    /** Rotate the live preview display (CSS). Does not affect the saved image. */
    previewRotation: number
    /** Rotate the saved/captured image pixels. Independent from previewRotation. */
    captureRotation: number
    autoBrightenLiveViewForFlash: boolean
    liveViewShutterSpeedOverride?: number
    postShutterLiveviewDelayMs?: number
  }
  setSecondaryCameraSettings: (settings: Partial<SettingsState['secondaryCameraSettings']>) => void
  enableScreensaver: boolean
  screensaverTimeout: number
  /** When true (browser only), reduces pull-to-refresh and overscroll navigation. Default off. */
  suppressBrowserGestures: boolean
  setSuppressBrowserGestures: (enabled: boolean) => void
  /**
   * Minimum seconds the processing screen stays open before Result after print work completes (0 = off).
   * Clamped 0–PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS.
   */
  printResultMinDisplaySeconds: number
  setPrintResultMinDisplaySeconds: (seconds: number) => void
  setSetting: (key: string, value: unknown) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      downloadLocal: true, // Default to true
      hasSeenSkipPaymentGuide: false,
      autoStart: true, // Default to true
      autoLaunch: false, // Default to false
      defaultProjectId: undefined,
      activationCode: '',
      deviceId: generateDeviceId(), // Initialize with a generated device ID
      debugFps: false, // Default to false
      useWebcam: true, // Default to true
      ejectCameraWhenIdle: false, // Default to false for all new users
      showCameraQualityWarnings: true, // Default to true to maintain current behavior
      cameraSettings: {
        captureRatio: 'auto' as const, // Changed to 'auto' for no cropping behavior
        mirror: true,
        useShutterCapture: false, // Default to false (use preview capture) - inverted from usePreviewAsCapture: true
        applyFilterOnPreview: true,
        previewQuality: {
          maxWidth: 1280, // Default medium quality
          maxHeight: 720,
          quality: 0.8,
          resizeQuality: 'medium' as ResizeQuality
        },
        captureQuality: {
          maxWidth: 1920, // Default high quality
          maxHeight: 1080,
          quality: 1,
          resizeQuality: 'high' as ResizeQuality
        },
        previewRotation: 0,
        captureRotation: 0,
        autoBrightenLiveViewForFlash: false,
        liveViewShutterSpeedOverride: undefined,
        postShutterLiveviewDelayMs: undefined
      },
      secondaryCameraSettings: {
        captureRatio: 'auto' as const, // Changed to 'auto' for no cropping behavior
        mirror: true,
        useShutterCapture: false, // Default to false (use preview capture) - inverted from usePreviewAsCapture: true
        applyFilterOnPreview: true,
        previewQuality: {
          maxWidth: 1280, // Default medium quality
          maxHeight: 720,
          quality: 0.8,
          resizeQuality: 'medium' as ResizeQuality
        },
        captureQuality: {
          maxWidth: 1920, // Default high quality
          maxHeight: 1080,
          quality: 1,
          resizeQuality: 'high' as ResizeQuality
        },
        previewRotation: 0,
        captureRotation: 0,
        autoBrightenLiveViewForFlash: false,
        liveViewShutterSpeedOverride: undefined,
        postShutterLiveviewDelayMs: undefined
      },
      setPassword: (password): void => void set({ password }),
      setSelectedPrinter: (printer): void => void set({ selectedPrinter: printer }),
      cameraConnectionKey: 0,
      incrementCameraConnectionKey: (): void =>
        void set((state) => ({ cameraConnectionKey: (state.cameraConnectionKey || 0) + 1 })),
      setSelectedCamera: (camera): void => void set({ selectedCamera: camera }),
      setSelectedSecondaryCamera: (camera): void => void set({ selectedSecondaryCamera: camera }),
      setDownloadLocal: (download): void => void set({ downloadLocal: download }),
      setHasSeenSkipPaymentGuide: (seen): void => void set({ hasSeenSkipPaymentGuide: seen }),
      setAutoStart: (enabled): void => void set({ autoStart: enabled }),
      setAutoLaunch: (enabled): void => void set({ autoLaunch: enabled }),
      setDefaultProjectId: (projectId): void => void set({ defaultProjectId: projectId }),
      setActivationCode: (code): void => void set({ activationCode: code }),
      setDebugFps: (enabled): void => void set({ debugFps: enabled }),
      setUseWebcam: (enabled): void => void set({ useWebcam: enabled }),
      setEjectCameraWhenIdle: (enabled): void => void set({ ejectCameraWhenIdle: enabled }),
      setShowCameraQualityWarnings: (enabled): void =>
        void set({ showCameraQualityWarnings: enabled }),
      enableScreensaver: false,
      screensaverTimeout: 60,
      suppressBrowserGestures: false,
      setSuppressBrowserGestures: (enabled): void => void set({ suppressBrowserGestures: enabled }),
      printResultMinDisplaySeconds: 0,
      setPrintResultMinDisplaySeconds: (seconds): void =>
        void set({
          printResultMinDisplaySeconds: Math.max(
            0,
            Math.min(PRINT_RESULT_MIN_DISPLAY_MAX_SECONDS, Math.floor(Number(seconds)) || 0)
          )
        }),
      setSetting: (key, value): void => void set({ [key]: value }),
      setCameraSettings: (settings): void =>
        void set((state) => ({
          cameraSettings: { ...state.cameraSettings, ...settings }
        })),
      setSecondaryCameraSettings: (settings): void =>
        void set((state) => ({
          secondaryCameraSettings: { ...state.secondaryCameraSettings, ...settings }
        }))
    }),
    {
      name: 'settings-v2', // Keep v2 to preserve other settings
      merge: (persistedState, currentState) => {
        const normalizeRotation = (value: unknown): number => {
          if (typeof value !== 'number' || Number.isNaN(value)) return 0
          const normalized = ((Math.round(value) % 360) + 360) % 360
          return normalized === 0 || normalized === 90 || normalized === 180 || normalized === 270
            ? normalized
            : 0
        }

        // Migration: Reset ejectCameraWhenIdle to false once for all users on first access
        // After that, respect their saved preference
        const persisted = persistedState as Partial<
          SettingsState & { _hasResetEjectCameraWhenIdle?: boolean; _hasMigratedShutterCapture?: boolean; _hasEnabledScreensaverByDefault?: boolean; _hasMigratedRotation?: boolean }
        >

        // Migration: Convert usePreviewAsCapture to useShutterCapture (invert logic)
        let migratedSettings = persisted || {}
        if (persisted && !persisted._hasMigratedShutterCapture) {
          // Migrate usePreviewAsCapture to useShutterCapture (invert boolean)
          if (persisted.cameraSettings && 'usePreviewAsCapture' in persisted.cameraSettings) {
            const oldValue = persisted.cameraSettings.usePreviewAsCapture as boolean
            migratedSettings = {
              ...persisted,
              cameraSettings: {
                ...persisted.cameraSettings,
                useShutterCapture: !oldValue, // Invert: true becomes false, false becomes true
                usePreviewAsCapture: undefined // Remove old property
              } as SettingsState['cameraSettings']
            }
          }
          if (persisted.secondaryCameraSettings && 'usePreviewAsCapture' in persisted.secondaryCameraSettings) {
            const oldValue = persisted.secondaryCameraSettings.usePreviewAsCapture as boolean
            migratedSettings = {
              ...migratedSettings,
              secondaryCameraSettings: {
                ...persisted.secondaryCameraSettings,
                useShutterCapture: !oldValue, // Invert: true becomes false, false becomes true
                usePreviewAsCapture: undefined // Remove old property
              } as SettingsState['secondaryCameraSettings']
            }
          }
          migratedSettings._hasMigratedShutterCapture = true
        }

        // Migration: Split old `rotation` field into previewRotation + captureRotation.
        // This is intentionally idempotent to avoid legacy values overriding user-configured
        // fields when migration flags are missing/corrupted.
        if (persisted) {
          const currentCamSettings = (migratedSettings.cameraSettings ||
            {}) as SettingsState['cameraSettings'] & { rotation?: number }
          const currentSecSettings = (migratedSettings.secondaryCameraSettings ||
            {}) as SettingsState['secondaryCameraSettings'] & { rotation?: number }
          const oldCamRotation = normalizeRotation(
            (persisted.cameraSettings as (SettingsState['cameraSettings'] & { rotation?: number }) | undefined)?.rotation
          )
          const oldSecRotation = normalizeRotation(
            (persisted.secondaryCameraSettings as (SettingsState['secondaryCameraSettings'] & { rotation?: number }) | undefined)?.rotation
          )
          const { rotation: _legacyCamRotation, ...camSettingsWithoutLegacyRotation } =
            currentCamSettings
          const { rotation: _legacySecRotation, ...secSettingsWithoutLegacyRotation } =
            currentSecSettings

          migratedSettings = {
            ...migratedSettings,
            cameraSettings: {
              ...camSettingsWithoutLegacyRotation,
              previewRotation:
                typeof currentCamSettings.previewRotation === 'number'
                  ? normalizeRotation(currentCamSettings.previewRotation)
                  : oldCamRotation,
              captureRotation:
                typeof currentCamSettings.captureRotation === 'number'
                  ? normalizeRotation(currentCamSettings.captureRotation)
                  : oldCamRotation
            } as SettingsState['cameraSettings'],
            secondaryCameraSettings: {
              ...secSettingsWithoutLegacyRotation,
              previewRotation:
                typeof currentSecSettings.previewRotation === 'number'
                  ? normalizeRotation(currentSecSettings.previewRotation)
                  : oldSecRotation,
              captureRotation:
                typeof currentSecSettings.captureRotation === 'number'
                  ? normalizeRotation(currentSecSettings.captureRotation)
                  : oldSecRotation
            } as SettingsState['secondaryCameraSettings'],
            _hasMigratedRotation: true
          }
        }

        // Migration: Preserve screensaver preference for existing users (default to false if unset)
        if (!migratedSettings._hasEnabledScreensaverByDefault) {
          migratedSettings = {
            ...migratedSettings,
            enableScreensaver: migratedSettings.enableScreensaver ?? false,
            _hasEnabledScreensaverByDefault: true
          }
        }

        if (migratedSettings && migratedSettings._hasResetEjectCameraWhenIdle) {
          // Migration already done, use saved preference
          return {
            ...currentState,
            ...migratedSettings,
            _hasEnabledScreensaverByDefault: true
          } as SettingsState & {
            _hasResetEjectCameraWhenIdle?: boolean
            _hasMigratedShutterCapture?: boolean
            _hasEnabledScreensaverByDefault?: boolean
            _hasMigratedRotation?: boolean
          }
        } else {
          // First time accessing after this change - reset to false and mark migration as done
          return {
            ...currentState,
            ...migratedSettings,
            ejectCameraWhenIdle: false,
            _hasResetEjectCameraWhenIdle: true,
            _hasEnabledScreensaverByDefault: true
          } as SettingsState & { _hasResetEjectCameraWhenIdle?: boolean; _hasMigratedShutterCapture?: boolean; _hasEnabledScreensaverByDefault?: boolean }
        }
      }
    }
  )
)
